import { Box, Option, Switch } from "@mui/joy";
import DarkSelect from "../../../common/DarkSelect";
import DarkInput from "../../../common/DarkInput";
import DarkTypography from "../../../common/DarkTypography";
import IconButtonCTA from "../../../common/IconButtonCTA";
import type { SMSTemplate } from "../../../../api/smsTemplates/SMSTemplate.typed";
import { SCreate, SDelete, SSave } from "../../../../styles/StyledMUI";
import { smsEditorToolbarRowSx } from "../smsEditorShared";

type SMSEditorToolbarProps = {
  templates: SMSTemplate[];
  template: SMSTemplate | undefined;
  onTemplateChange: (next: SMSTemplate) => void;
  loading: boolean;
  saving: boolean;
  canEdit: boolean;
  onSelectTemplateId: (id: number) => void;
  onNewTemplate: () => void;
  onSave: () => void;
  onDelete: () => void;
  onActiveChange: (active: boolean) => void;
};

const toolbarGridSx = {
  ...smsEditorToolbarRowSx,
  display: "grid",
  gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1fr) minmax(160px, 280px) minmax(0, 1fr)" },
  alignItems: "center",
  columnGap: 1,
  rowGap: 1,
  flexWrap: "nowrap",
} as const;

const toolbarClusterSx = {
  display: "flex",
  flexWrap: "wrap" as const,
  alignItems: "center",
  gap: 1,
  minWidth: 0,
} as const;

export default function SMSEditorToolbar(props: SMSEditorToolbarProps) {
  const {
    templates,
    template,
    onTemplateChange,
    loading,
    saving,
    canEdit,
    onSelectTemplateId,
    onNewTemplate,
    onSave,
    onDelete,
    onActiveChange,
  } = props;

  const showDraft = template != null && template.id == null;
  const selectValue = template?.id != null ? String(template.id) : showDraft ? "draft" : "";

  return (
    <Box sx={toolbarGridSx}>
      <Box sx={toolbarClusterSx}>
        <Box
          sx={{
            minWidth: 160,
            maxWidth: { xs: "100%", sm: 280 },
            flex: { xs: "1 1 160px", sm: "1 1 180px" },
          }}
        >
          <DarkSelect
            value={selectValue || null}
            placeholder="Aucun modèle"
            disabled={loading}
            onChange={(_e, val) => {
              const v = String(val || "");
              if (!v || v === "draft") return;
              onSelectTemplateId(Number(v));
            }}
          >
            {showDraft && <Option value="draft">Nouveau modèle</Option>}
            {templates.map((tpl) => (
              <Option key={tpl.id} value={String(tpl.id)}>
                {tpl.name || `Modèle #${tpl.id}`}
                {!tpl.active ? " (inactif)" : ""}
              </Option>
            ))}
          </DarkSelect>
        </Box>

        {template?.id != null && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexShrink: 0 }}>
            <DarkTypography level="body-sm" colorVariant="primary" sx={{ whiteSpace: "nowrap" }}>
              Actif
            </DarkTypography>
            <Switch
              size="sm"
              checked={!!template.active}
              disabled={!canEdit || loading || saving}
              onChange={(e) => onActiveChange(e.target.checked)}
              slotProps={{
                input: { "aria-label": "Actif pour l’envoi" },
              }}
            />
          </Box>
        )}
      </Box>

      <Box
        sx={{
          width: "100%",
          minWidth: 0,
          justifySelf: "center",
        }}
      >
        {template ? (
          <DarkInput
            value={template.name}
            disabled={!canEdit}
            placeholder="Nom du modèle"
            aria-label="Nom du modèle"
            onChange={(e) => onTemplateChange({ ...template, name: e.target.value })}
            sx={{
              width: "100%",
              "& input": { textAlign: "center" },
            }}
          />
        ) : null}
      </Box>

      <Box sx={{ ...toolbarClusterSx, justifyContent: { xs: "flex-start", md: "flex-end" } }}>
        {canEdit && (
          <>
            <IconButtonCTA
              aria-label="Nouveau modèle SMS"
              size="sm"
              ctaVariant="accent"
              disabled={loading || saving}
              onClick={onNewTemplate}
            >
              <SCreate />
            </IconButtonCTA>
            {template?.id != null && (
              <IconButtonCTA
                aria-label="Supprimer le modèle"
                size="sm"
                ctaVariant="danger"
                disabled={loading || saving}
                onClick={() => void onDelete()}
              >
                <SDelete />
              </IconButtonCTA>
            )}
            {template && (
              <IconButtonCTA
                aria-label={template.id ? "Enregistrer le modèle" : "Créer le modèle"}
                ctaVariant="success"
                disabled={loading || saving}
                onClick={() => void onSave()}
              >
                <SSave />
              </IconButtonCTA>
            )}
          </>
        )}
      </Box>
    </Box>
  );
}
